import { useState } from 'react';
import { AsyncBoundary, ConfirmDialog, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useRooms, useCreateRoom, useDeleteRoom, useBookRoom,
  type RoomRow, type RoomBooking,
} from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';
import { useAuthStore } from '@/lib/authStore';

/**
 * /tpo/rooms (Page 78/N) — interview rooms and labs.
 *
 * Rooms feed the interview scheduler + drive-day capacity planning.
 * Was in the audit backlog as "Warn #2: rooms CRUD" — API was live
 * but no UI consumed it.
 */

export function TpoRoomsPage() {
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteRow, setDeleteRow] = useState<RoomRow | null>(null);
  const [bookRow, setBookRow] = useState<RoomRow | null>(null);

  const [bookFrom, setBookFrom] = useState('');
  const [bookTo, setBookTo] = useState('');
  const [bookPurpose, setBookPurpose] = useState<RoomBooking['purpose']>('interview');
  const [bookNote, setBookNote] = useState('');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [capacity, setCapacity] = useState('');
  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [features, setFeatures] = useState('');

  const list = useRooms({ q, limit: 200 });
  const create = useCreateRoom();
  const del = useDeleteRoom();
  const book = useBookRoom();
  const rows = list.data ?? [];

  const openBook = (room: RoomRow) => {
    setBookRow(room);
    // Default: tomorrow 10:00 → 11:00 (Asia/Kolkata local)
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    const iso = (x: Date) => {
      const off = x.getTimezoneOffset();
      const local = new Date(x.getTime() - off * 60_000);
      return local.toISOString().slice(0, 16);
    };
    setBookFrom(iso(d));
    d.setHours(11);
    setBookTo(iso(d));
    setBookPurpose('interview');
    setBookNote('');
  };

  const submitBook = () => {
    if (!bookRow) return;
    const from = new Date(bookFrom);
    const to = new Date(bookTo);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      toast.show({ tone: 'warning', message: 'Pick a valid window.' });
      return;
    }
    if (to <= from) {
      toast.show({ tone: 'warning', message: 'End time must be after start.' });
      return;
    }
    book.mutate(
      {
        id: bookRow._id,
        from: from.toISOString(),
        to: to.toISOString(),
        purpose: bookPurpose,
        note: bookNote.trim() || undefined,
      },
      {
        onSuccess: () => {
          setBookRow(null);
          toast.show({ message: `Booked ${bookRow.name}.` });
        },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  const resetForm = () => {
    setName(''); setCode(''); setCapacity(''); setBuilding(''); setFloor(''); setFeatures('');
  };

  const submit = () => {
    if (!user?.college || !name.trim()) return;
    create.mutate(
      {
        college: user.college,
        name: name.trim(),
        code: code.trim() || undefined,
        capacity: capacity.trim() ? Number(capacity) : undefined,
        building: building.trim() || undefined,
        floor: floor.trim() || undefined,
        features: features.split(',').map((s) => s.trim()).filter(Boolean),
      },
      {
        onSuccess: () => { setCreating(false); resetForm(); toast.show({ message: 'Room added.' }); },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleteRow) return;
    del.mutate(deleteRow._id, {
      onSuccess: () => { setDeleteRow(null); toast.show({ message: 'Removed.' }); },
      onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
    });
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Calendar</p>
          <h1 className="h-page">Rooms</h1>
          <p className="subhead">
            Interview rooms, labs, and halls. The scheduler places panels here on drive
            days, and lists them on interview invitations.
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>Add room</button>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search name or code" value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="fb-count">{rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No rooms yet"
          emptyHint="Add your interview halls, faculty rooms, and labs so the scheduler can book them."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Name</th><th>Code</th><th>Location</th><th>Capacity</th><th>Features</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td className="cell-title">{r.name}</td>
                    <td className="cell-sub">{r.code ?? '—'}</td>
                    <td className="cell-sub">{[r.building, r.floor].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="cell-sub">{r.capacity ?? r.seats ?? '—'}</td>
                    <td className="cell-sub">
                      {r.features && r.features.length > 0 ? r.features.join(', ') : '—'}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1 flex-wrap">
                        <button className="btn btn-sm btn-primary" onClick={() => openBook(r)}>
                          Book
                        </button>
                        <button className="btn btn-sm btn-ghost btn-danger" onClick={() => setDeleteRow(r)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Capacity is what shows on the invite.</b> Panels don't check capacity themselves —
        the scheduler does, at book time.
      </Notice>

      <Drawer open={creating} onClose={() => setCreating(false)} title="Add room" size="md">
        <div className="stack">
          <label className="block">
            <span className="eyebrow">Name</span>
            <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Placement Hall" />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="eyebrow">Code (optional)</span>
              <input className="input mt-1" value={code} onChange={(e) => setCode(e.target.value)} placeholder="PH-01" />
            </label>
            <label className="block">
              <span className="eyebrow">Capacity</span>
              <input className="input mt-1" type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
            </label>
            <label className="block">
              <span className="eyebrow">Building</span>
              <input className="input mt-1" value={building} onChange={(e) => setBuilding(e.target.value)} />
            </label>
            <label className="block">
              <span className="eyebrow">Floor</span>
              <input className="input mt-1" value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="2" />
            </label>
          </div>
          <label className="block">
            <span className="eyebrow">Features (comma-sep)</span>
            <input className="input mt-1" value={features} onChange={(e) => setFeatures(e.target.value)}
              placeholder="projector, wifi, whiteboard" />
          </label>
          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={!name.trim() || create.isPending}
              onClick={submit}
            >
              {create.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer open={Boolean(bookRow)} onClose={() => setBookRow(null)} title={bookRow ? `Book ${bookRow.name}` : 'Book room'} size="md">
        {bookRow && (
          <div className="stack">
            <Notice tone="info">
              Reservations are exclusive — a window overlap is rejected server-side. Existing
              bookings on this room show below for context.
            </Notice>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="eyebrow">From</span>
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={bookFrom}
                  onChange={(e) => setBookFrom(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="eyebrow">To</span>
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={bookTo}
                  onChange={(e) => setBookTo(e.target.value)}
                />
              </label>
            </div>

            <label className="block">
              <span className="eyebrow">Purpose</span>
              <select
                className="input mt-1"
                value={bookPurpose}
                onChange={(e) => setBookPurpose(e.target.value as RoomBooking['purpose'])}
              >
                <option value="interview">Interview</option>
                <option value="drive">Drive</option>
                <option value="exam">Exam</option>
                <option value="class">Class</option>
                <option value="other">Other</option>
              </select>
            </label>

            <label className="block">
              <span className="eyebrow">Note (optional)</span>
              <input
                className="input mt-1"
                value={bookNote}
                onChange={(e) => setBookNote(e.target.value)}
                placeholder="e.g. Wipro panel A · 3rd round"
              />
            </label>

            {bookRow.bookings && bookRow.bookings.length > 0 && (
              <div>
                <div className="eyebrow mb-1">Existing bookings ({bookRow.bookings.length})</div>
                <ul className="cell-sub grid gap-1">
                  {[...bookRow.bookings]
                    .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime())
                    .slice(0, 8)
                    .map((b, i) => (
                      <li key={i}>
                        {formatIndianDate(new Date(b.from))}{' '}
                        {new Date(b.from).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        {' → '}
                        {new Date(b.to).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        {' · '}
                        <span className="badge">{b.purpose}</span>
                        {b.note ? ` — ${b.note}` : ''}
                      </li>
                    ))}
                  {bookRow.bookings.length > 8 && (
                    <li className="cell-sub">…{bookRow.bookings.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setBookRow(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={book.isPending}
                onClick={submitBook}
              >
                {book.isPending ? 'Booking…' : 'Book'}
              </button>
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={confirmDelete}
        title="Remove room?"
        description={deleteRow ? `"${deleteRow.name}" will no longer be bookable. Existing bookings won't be affected.` : ''}
        confirmLabel="Remove"
        tone="danger"
      />
    </div>
  );
}
